import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            {t("categoriesEyebrow")}
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t("categoriesTitle")}
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm md:text-base text-foreground-600">
            {t("categoriesText")}
          </p>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="relative w-full max-w-md" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer hover:border-primary-400 transition-colors"
            >
              <span className="flex items-center gap-2">
                <i className="ri-price-tag-3-line text-base text-foreground-500" />
                {t("dropdownPlaceholder")}
              </span>
              <i className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <div className="absolute z-20 mt-2 w-full max-h-72 overflow-y-auto rounded-lg border border-background-200 bg-background-50 shadow-sm">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-sm text-foreground-400" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.href}
              target="_blank"
              rel="noopener nofollow"
              className="group block cursor-pointer"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-background-100">
                <img
                  src={cat.image}
                  alt={cat.name}
                  title={`${cat.name} — Basetao Spreadsheet category`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </span>
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className="ri-arrow-right-up-line text-xs" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}